import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Upload, LogOut, Building2, Shield, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/state/auth';

interface CommandItemDef {
  id: string;
  group: string;
  icon: LucideIcon;
  title: string;
  sub?: string;
  action: (nav: ReturnType<typeof useNavigate>) => void;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focus, setFocus] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();
  const user = useAuth((s) => s.user);

  // Открытие/закрытие: ⌘K / Ctrl+K и Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-command-palette', onOpenEvent);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setFocus(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const items: CommandItemDef[] = useMemo(() => {
    const list: CommandItemDef[] = [
      { id: 'a-projects', group: 'Действия', icon: Plus,     title: 'Создать замечание',  sub: 'откройте проект → «+ Замечание»', action: (n) => n('/projects') },
      { id: 'a-upload',   group: 'Действия', icon: Upload,   title: 'Загрузить редакцию', sub: 'откройте проект → Документы',     action: (n) => n('/projects') },
      { id: 'a-logout',   group: 'Действия', icon: LogOut,   title: 'Выйти',              sub: 'завершить сессию',                action: () => useAuth.getState().logout() },

      { id: 'g-projects', group: 'Перейти', icon: Building2, title: 'Проекты',      sub: 'список проектов', action: (n) => n('/projects') },
    ];
    if ((useAuth.getState().activeRole || user?.default_role) === 'admin') {
      list.push({ id: 'g-admin', group: 'Перейти', icon: Shield, title: 'Админ-панель', sub: 'управление пользователями', action: (n) => n('/admin') });
    }
    return list;
  }, [user]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      it.title.toLowerCase().includes(q) || (it.sub && it.sub.toLowerCase().includes(q))
    );
  }, [items, query]);

  // Группируем для рендера
  const groups = useMemo(() => {
    const m = new Map<string, CommandItemDef[]>();
    for (const it of filtered) {
      if (!m.has(it.group)) m.set(it.group, []);
      m.get(it.group)!.push(it);
    }
    return Array.from(m.entries());
  }, [filtered]);

  const flat = filtered;

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault(); setFocus((f) => Math.min(f + 1, flat.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault(); setFocus((f) => Math.max(f - 1, 0));
      } else if (e.key === 'Enter' && flat[focus]) {
        e.preventDefault();
        flat[focus].action(nav);
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flat, focus, nav]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] backdrop-blur-sm"
        style={{ background: 'rgba(10,22,40,.6)' }}
        onClick={() => setOpen(false)}
      />
      <div
        className="fixed bg-white rounded-card flex flex-col border border-border z-[60]"
        style={{
          top: '10%', left: '50%', transform: 'translateX(-50%)',
          width: 'calc(100% - 32px)', maxWidth: 640,
          boxShadow: '0 24px 64px rgba(10,22,40,.24)',
          overflow: 'hidden',
        }}
      >
        <div className="flex items-center border-b border-border" style={{ padding: 16, gap: 12 }}>
          <Search size={20} stroke="#6B7280" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setFocus(0); }}
            placeholder="Поиск проектов, замечаний, действий…"
            className="flex-1 text-ink outline-none"
            style={{ fontSize: 16, fontFamily: 'Inter, system-ui, sans-serif', background: 'transparent', border: 'none' }}
          />
          <span className="rounded-pill bg-surface border border-border text-muted" style={{ padding: '2px 8px', fontSize: 11 }}>esc</span>
        </div>

        <div style={{ maxHeight: 420, overflowY: 'auto', padding: 8 }}>
          {flat.length === 0 && (
            <p className="text-muted text-center" style={{ padding: '24px 0', fontSize: 13 }}>Ничего не найдено</p>
          )}
          {(() => {
            let cursor = 0;
            return groups.map(([groupName, groupItems]) => (
              <div key={groupName}>
                <div className="text-muted uppercase tracking-wider" style={{ padding: '10px 12px 6px', fontSize: 11, fontWeight: 600 }}>{groupName}</div>
                {groupItems.map((it) => {
                  const idx = cursor++;
                  const Icon = it.icon;
                  const isActive = idx === focus;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => { it.action(nav); setOpen(false); }}
                      onMouseEnter={() => setFocus(idx)}
                      className="w-full text-left rounded-btn flex items-center"
                      style={{
                        padding: '10px 12px', gap: 12,
                        background: isActive ? '#F3F4F6' : 'transparent',
                        border: 'none', cursor: 'pointer',
                      }}
                    >
                      <Icon size={18} stroke="#6B7280" className="shrink-0" />
                      <div style={{ flex: 1 }}>
                        <div className="text-ink" style={{ fontSize: 14, fontWeight: 500 }}>{it.title}</div>
                        {it.sub && <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>{it.sub}</div>}
                      </div>
                      <span className="text-muted tabular" style={{ fontSize: 11 }}>↵</span>
                    </button>
                  );
                })}
              </div>
            ));
          })()}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-surface" style={{ padding: '8px 16px' }}>
          <span className="text-muted" style={{ fontSize: 11 }}>↑↓ навигация · ↵ выбрать · esc закрыть</span>
          <span className="text-muted tabular" style={{ fontSize: 11 }}>Коллегиум v2</span>
        </div>
      </div>
    </>
  );
}
